/**
 * @module Table
 * @description A complex component for rendering and managing interactive tables in the Conference Room Designer.
 * It handles table generation, movement, rotation, and microphone path calculations for tabletop microphones.
 */
import styled from "styled-components";
import { useEffect, useState, useMemo, useRef, useLayoutEffect, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReducersIndex from 'reduxModules/ducks';
import { getConditionResult, getCurrentValue2 } from "reduxModules/ducks/container";
import { getDrawingBox } from "reduxModules/ducks/ui";
import ToolContext from "components/services/ToolContext";
import { Chair } from "globalConstants/Icons/Furniture";
import { Move, Rotate } from "globalConstants/Icons/Actions";
import { iconThemes } from "globalConstants/Themes";
import useMousePosition from "hooks/useMousePosition";
import { mapValues, clamp, round, floor, range, assignWith, values, isFunction, min, max, minBy, maxBy, some, pick, difference, indexOf, isEqual, isEmpty, every, keys } from "lodash";
import { quadraticBezier } from "utilities/mathematic";
import { getCoverageBoxes } from "reduxModules/ducks/room";

/**
 * @constant TableConstants
 * @description Contains various constants and configuration objects for table and chair properties.
 * @property {Object} tables - Configurations for different table shapes (rectangle, racetrack, boat, etc.)
 * @property {Object} chair - Constants for chair properties (size, clearance, state)
 * @property {Object} microphones - Constants for microphone positioning
 */
export const constants = {
  tables: {
    rectangle: {
      pathProps: ({ length, width }) => {
        const corner = min([length * 0.03, width * .15])
        return {
          corner: corner,
          length: length - 2 * corner, //  * .94,
          width: width - 2 * corner //length * 0.06,
        }
      },
      chairs: ({ length, width } = 'tableSize', chair) => {
        const qtyX = floor(length / chair.size);
        const spaceX = qtyX ? length / qtyX : 0;
        const firstX = (spaceX - length) / 2;
        const y = width / 2 + chair.clearance;
        const chairs = range(firstX, length / 2, spaceX).flatMap((x, i) => [{ id: `chair-bottom${i}`, x: x, y: y, a: 90 }, { id: `chair-top${i}`, x: x, y: -y, a: -90 }]);
        const qtyY = floor(width / chair.size);
        const spaceY = qtyY ? width / qtyY : 0;
        const firstY = (spaceY - width) / 2;
        const x = length / 2 + chair.clearance;
        const chairArray = chairs.concat(
          range(firstY, width / 2, spaceY).flatMap((y, i) => [{ id: `chair-right${i}`, x: x, y: y, a: 0 }, { id: `chair-left${i}`, x: -x, y: y, a: 180 }])
        );
        return {
          chairArray,
          chairSpacing: { x: spaceX, y: spaceY }
        }
      },
      devicePathProps: (tableSize, chairSpacing, tableBox, deviceFromEdge, deviceCoverageRadius) => {
        let devicePathLength = tableSize.length - 2 * deviceFromEdge;
        let devicePathWidth = tableSize.width - 2 * deviceFromEdge;
        if (!isEmpty(tableBox) && deviceCoverageRadius * 2 >= tableBox.x) {
          devicePathLength = 0;
          devicePathWidth = Math.floor(devicePathWidth / chairSpacing.y) * chairSpacing.y
        };
        if (!isEmpty(tableBox) && deviceCoverageRadius * 2 >= tableBox.y) {
          devicePathWidth = 0;
          devicePathLength = Math.floor(devicePathLength / chairSpacing.x) * chairSpacing.x
        };
        return { devicePathLength, devicePathWidth }
      },
    },
    racetrack: {
      pathProps: ({ length, width }) => ({
        corner: (width <= length ? width : length) / 2,
        length: width <= length ? length - width : width - length,
        width: 0,
      }),
      chairs: (tableSize, chair) => {

        const length = Math.max(...values(tableSize));
        const width = Math.min(...values(tableSize));
        const radius = width / 2;
        const straightLength = length - width; // Length of straight sections combined
        const halfStraight = straightLength / 2;
        const chairRadius = radius + chair.clearance;

        // Calculate chairs for straight sections
        const straightSectionChairs = [];

        // Number of chairs on straight sections (top and bottom)
        const qtyX = Math.floor(straightLength / chair.size);
        if (qtyX > 0) {
          const spaceX = straightLength / qtyX;
          for (let i = 0; i < qtyX; i++) {
            const x = -halfStraight + (i + 0.5) * spaceX;
            straightSectionChairs.push(
              { id: `chair-top${i}`, x: x, y: -chairRadius, a: -90 },
              { id: `chair-bottom${i}`, x: x, y: chairRadius, a: 90 }
            );
          }
        }

        // Calculate chairs for curved sections
        const curvedSectionChairs = [];

        // Number of chairs per curved section (each sedeviceircle)
        const arcLength = Math.PI * radius;
        const chairsPerSedeviceircle = Math.max(3, Math.floor(arcLength / chair.size));
        const angleStep = Math.PI / chairsPerSedeviceircle;

        // Adjust the starting angle to avoid overlap with straight section chairs
        // Instead of starting exactly at -π/2 or π/2, start at -π/2 + angleStep/2 and π/2 + angleStep/2
        const startAngleOffset = angleStep / 2;

        // Right curved section (adjusted range to avoid overlap)
        for (let i = 0; i < chairsPerSedeviceircle; i++) {
          const angle = -Math.PI / 2 + startAngleOffset + i * angleStep;
          curvedSectionChairs.push({
            id: `chair-right${i}`,
            x: halfStraight + chairRadius * Math.cos(angle),
            y: chairRadius * Math.sin(angle),
            a: angle * 180 / Math.PI
          });
        }

        // Left curved section (adjusted range to avoid overlap)
        for (let i = 0; i < chairsPerSedeviceircle; i++) {
          const angle = Math.PI / 2 + startAngleOffset + i * angleStep;
          curvedSectionChairs.push({
            id: `chair-left${i}`,
            x: -halfStraight + chairRadius * Math.cos(angle),
            y: chairRadius * Math.sin(angle),
            a: angle * 180 / Math.PI
          });
        }

        // Combine all chairs
        const chairArray = [...straightSectionChairs, ...curvedSectionChairs];

        return {
          chairArray,
          chairSpacing: { x: qtyX > 0 ? straightLength / qtyX : chairRadius }
        };
      },
      devicePathProps: (tableSize, chairSpacing, tableBox, deviceFromEdge, deviceCoverageRadius) => {
        const isHorizontal = tableSize.width <= tableSize.length;
        let devicePathCorner = ((isHorizontal ? tableSize.width : tableSize.length) / 2) - deviceFromEdge;
        let devicePathLength = (isHorizontal ? tableSize.length : tableSize.width) - 2 * deviceFromEdge - 2 * devicePathCorner;
        if (!isEmpty(tableBox) && deviceCoverageRadius * 2 >= tableBox.x) {
          devicePathCorner = isHorizontal ? devicePathCorner : 0;
          devicePathLength = isHorizontal ? 0 : devicePathLength;
        };
        if (!isEmpty(tableBox) && deviceCoverageRadius * 2 >= tableBox.y) {
          devicePathCorner = 0;
        };
        return { devicePathLength, devicePathCorner, devicePathRightCorner: devicePathCorner }
      },
    },
    boat: {
      pathProps: ({ length, width }) => ({
        corner: length * .015,
        bow: length * .1,
        length: length * .97,
        width: width - length * 0.13
      }),
      chairs: ({ length, width } = 'tableSize', chair) => {
        const qtyX = floor(length / chair.size);
        const spaceX = qtyX ? length / qtyX : 0;
        const firstX = (spaceX - length) / 2;
        const baseY = (width - length * 0.1) / 2 + chair.clearance;
        const points = [{ x: 0, y: 0 }, { x: length / 2, y: length * .1 }, { x: length, y: 0 }];
        const chairs = range(firstX, length / 2, spaceX).flatMap((x, i) => {
          const { curveY, angle } = quadraticBezier(x + length / 2, points);
          const y = baseY + curveY;
          const a = 90 + angle;
          return [{ id: `chair-bottom${i}`, x: x, y: y, a: a }, { id: `chair-top${i}`, x: x, y: -y, a: -a }]
        });
        const short = (width - length * .13);
        const qtyY = floor(short / chair.size);
        const spaceY = short / qtyY;
        const firstY = (spaceY - short) / 2;
        const x = length / 2 + chair.clearance;
        const chairArray = chairs.concat(
          range(firstY, qtyY ? short / 2 : 0, spaceY).flatMap((y, i) => [{ id: `chair-right${i}`, x: x, y: y, a: 0 }, { id: `chair-left${i}`, x: -x, y: y, a: 180 }])
        );
        return {
          chairArray,
          chairSpacing: { x: spaceX, y: spaceY }
        }
      },
      devicePathProps: (tableSize, chairSpacing, tableBox, deviceFromEdge, deviceCoverageRadius) => {
        let devicePathLength = tableSize.length - 2 * deviceFromEdge;
        let devicePathWidth = (tableSize.width - tableSize.length * .1) - 2 * deviceFromEdge;
        let devicePathBow = devicePathLength * .1;
        if (!isEmpty(tableBox) && deviceCoverageRadius * 2 >= tableBox.x) {
          devicePathLength = 0;
          devicePathWidth = Math.floor(devicePathWidth / chairSpacing.y) * chairSpacing.y
        };
        if (!isEmpty(tableBox) && deviceCoverageRadius * 2 >= tableBox.y) {
          devicePathWidth = 0;
          devicePathBow = 0;
          devicePathLength = Math.floor(devicePathLength / chairSpacing.x) * chairSpacing.x
        };
        return { devicePathLength, devicePathWidth, devicePathBow }
      },
    },
    round: {
      pathProps: ({ length, width }) => ({
        corner: length / 2,
        length: 0,
        width: 0,
      }),
      chairs: ({ length } = 'tableSize', chair) => {
        const qty = floor(Math.PI * length / chair.size);
        const radius = length / 2 + chair.clearance;
        const angle = 2 * Math.PI / qty;
        const chairArray = range(0, Math.PI * 2, angle).map((a, i) => ({ id: `chair-${i}`, x: radius * Math.cos(a), y: radius * Math.sin(a), a: a * 180 / Math.PI }));
        return {
          chairArray,
          chairSpacing: { x: 1, y: 1 }
        }
      },
      devicePathProps: (tableSize, chairSpacing, tableBox, deviceFromEdge, deviceCoverageRadius) => {
        let devicePathRightCorner = tableSize.length / 2 - deviceFromEdge;
        if (!isEmpty(tableBox) && devicePathRightCorner * 2 >= tableBox.x) {
          devicePathRightCorner = 0;
        };
        return { devicePathRightCorner, devicePathCorner: devicePathRightCorner }
      },
    },
    bullet: {
      pathProps: ({ length, width }) => {
        const corner = min([length * 0.03, width * .10]);
        return {
          leftCorner: corner,
          rightCorner: width / 2,
          length: length - corner - width / 2, //  * 0.97 - width / 2 ,
          width: width - 2 * corner //- length * 0.06
        }
      },
      chairs: ({ length, width } = 'tableSize', chair) => {
        const radius = width / 2;
        const short = length - radius;
        const qtyX = floor(short / chair.size);
        const spaceX = short / qtyX;
        const firstX = (spaceX - length) / 2;
        const y = radius + chair.clearance;
        const chairs = range(firstX, qtyX ? (short - radius) / 2 : 0, spaceX).flatMap((x, i) => [{ id: `chair-bottom${i}`, x: x, y: y, a: 90 }, { id: `chair-top${i}`, x: x, y: -y, a: -90 }]);
        const qtyY = floor(Math.PI * radius / chair.size);
        const chairRadius = radius + chair.clearance;
        const angle = Math.PI / qtyY;
        const baseX = length / 2 - radius;
        const chairArray = chairs.concat(
          range(angle / 2 - Math.PI / 2, Math.PI / 2, angle).map((a, i) => ({ id: `chair-right${i}`, x: baseX + chairRadius * Math.cos(a), y: chairRadius * Math.sin(a), a: a * 180 / Math.PI }))
        );
        return {
          chairArray,
          chairSpacing: { x: spaceX, y: 1 }
        };
      },
      devicePathProps: (tableSize, chairSpacing, tableBox, deviceFromEdge, deviceCoverageRadius) => {
        let devicePathRightCorner = tableSize.width / 2 - deviceFromEdge;
        let devicePathLength = Math.max(0, tableSize.length - 2 * deviceFromEdge - devicePathRightCorner);
        if (!isEmpty(tableBox) && deviceCoverageRadius * 2 >= tableBox.y) {
          devicePathRightCorner = 0;
        };
        return { devicePathLength, devicePathRightCorner }
      },
    },
    keystone: {
      maxSlope: .3,
      pathProps: ({ length, width }) => ({
        corner: length * 0.015,
        length: length * .97,
        width: width - length * 0.03,
      }),
      chairs: ({ length, width } = 'tableSize', chair, slope = 0) => {
        const tapper = length * slope * .94;
        const qtyX = floor(length / chair.size);
        const spaceX = qtyX ? length / qtyX : 0;
        const firstX = (spaceX - length) / 2 + chair.size * slope / 4;
        const baseY = (width - tapper) / 2 + chair.clearance;
        const angle = slope * 180 / Math.PI;
        const chairs = range(firstX, length / 2, spaceX).flatMap((x, i) => {
          const y = baseY - x * slope;
          const a = 90 - angle;
          return [{ id: `chair-bottom${i}`, x: x, y: y, a: a }, { id: `chair-top${i}`, x: x, y: -y, a: -a }]
        });
        const short = width - 2 * tapper;
        const qtyY = floor(short / chair.size);
        const spaceY = qtyY ? short / qtyY : 0;
        const firstY = (spaceY - short) / 2;
        const x = length / 2 + chair.clearance;
        const chairArray = chairs.concat(
          range(firstY, short / 2, spaceY).map((y, i) => ({ id: `chair-right${i}`, x: x, y: y, a: 0 }))
        );
        return {
          chairArray,
          chairSpacing: { x: spaceX, y: spaceY }
        };
      },
      devicePathProps: (tableSize, chairSpacing, tableBox, deviceFromEdge, deviceCoverageRadius) => {
        let devicePathLength = tableSize.length - 2 * deviceFromEdge;
        let devicePathWidth = tableSize.width - 2 * deviceFromEdge;
        if (!isEmpty(tableBox) && deviceCoverageRadius * 2 >= tableBox.y) {
          devicePathWidth = 0;
          devicePathLength = Math.floor(devicePathLength / chairSpacing.x) * chairSpacing.x
        };
        return { devicePathLength, devicePathWidth }
      },
    },
    ushape: {
      notched: true,
      pathProps: ({ length, width }) => ({
        corner: length * 0.015,
        length: length * 0.97,
        width: width - length * 0.03,
      }),
      chairs: (tableSize, chair) => {
        let { chairArray, chairSpacing } = constants.tables.rectangle.chairs(tableSize, chair)
        chairArray = chairArray.filter(chair => !chair.id.includes('-l'));
        return { chairArray, chairSpacing }
      },
      devicePathProps: (tableSize, chairSpacing, tableBox, deviceFromEdge, deviceCoverageRadius, legWidth) => {
        let devicePathLength = tableSize.length - 2 * deviceFromEdge;
        let devicePathWidth = tableSize.width - 2 * deviceFromEdge;
        if (tableSize.width <= 2 * legWidth) {
          devicePathWidth = 0;
          devicePathLength = Math.floor(devicePathLength / chairSpacing.x) * chairSpacing.x
        };
        return { devicePathLength, devicePathWidth }
      },
    },
    vshape: {
      notched: true,
      maxSlope: .63,
      pathProps: ({ length, width }) => ({
        corner: length * 0.0075,
        length: length * .985,
        width: width - length * 0.015,
      }),
      chairs: (tableSize, chair, slope) => constants.tables.keystone.chairs(tableSize, chair, slope),
      devicePathProps: (tableSize, chairSpacing, tableBox, deviceFromEdge, deviceCoverageRadius, legWidth, isTableMic = false) => {
        let devicePathLength = tableSize.length - 2 * deviceFromEdge;
        let devicePathWidth = tableSize.width - 2 * deviceFromEdge;
        // if (tableSize.width <= 2 * legWidth) {
          if (!isEmpty(tableBox) && !isTableMic && deviceCoverageRadius * 2 >= tableBox.y) {
          devicePathWidth = 0;
          devicePathLength = Math.floor(devicePathLength / chairSpacing.x) * chairSpacing.x
        };
        return { devicePathLength, devicePathWidth }
      },
    },
  },
  chair: {
    size: 0.762, // size of chair 30" in meters
    clearance: 0.1905, // 25% chair clearance from table .25 of chair is under table
    state: (ghosted, filled) => ({
      border_fill: filled ? 'var(--app-light-gray)' : 'none',
      icon_stroke: ghosted ? filled ? 'var(--app-custom-gray)' : 'none' : 'var(--app-dark-gray)',
      opacity: ghosted ? .2 : 1,
      dash: ghosted ? 4 : 0
    })
  },
  deviceToEdge: (isTableMic = true, index = 2) => isTableMic ? 0.4 : [0.238, 0.538, 0.938, 1.448, 1.738][index]
};

/**
 * @function Table
 * @description Renders an interactive table with chairs and microphone paths.
 * @param {Object} props - Component props
 * @returns {React.Element} The rendered Table component
 */
const Table = (props) => {
  const areaId = useSelector(state => getCurrentValue2(state, 'activeTab'));
  const { bbox, scale } = useSelector(getDrawingBox);
  const currentTable = useSelector(state => getCurrentValue2(state, 'table'));
  const tableSize = mapValues(currentTable.size, (v) => v * scale);
  const chair = mapValues(constants.chair, (v) => isFunction(v) ? v : v * scale);
  const iconScale = chair.size * .0072;
  const iconMargin = 36; //30 * iconScale; // Icon base size 46 
  const context = useContext(ToolContext)
  const table2wall = context.constants.area.table2wall * scale;
  const [tableCenter, setTableCenter] = useState({ x: 0, y: 0 });
  const [tableTransform, setTableTransfrom] = useState({ x: 0, y: 0 });
  const [iconTransform, setIconTransform] = useState({ x: 0, y: 0, scale: 0 });
  const { notched = false, maxSlope = null, pathProps = () => ({}), chairs = () => [], devicePathProps = () => ({}) } = constants.tables[currentTable.shape] ?? {};
  const { endTable = false, minimum: minimumTableSize = {length: 0, width: 0} } = context.constants.tables[currentTable.shape] ?? {};

  // Get data related to tablemic to create the mic path
  const { model: micModel } = useSelector(state => getCurrentValue2(state, 'microphones'));
  const microphoneModel = micModel.replace('mic', '_mic');
  const { model, height } = useSelector(state => getCurrentValue2(state, 'speakers'));

  const { equipment: { microphones: { [microphoneModel]: { micData, isTableMic = false } }, speakers: { [model]: { coverageAngle } } }, acoustics, area: { talkerHeight } } = context.constants;
  const currentAcoustics = useSelector(state => getCurrentValue2(state, 'acoustics'));
  const micCoverageRadius = micData[indexOf(acoustics, currentAcoustics)] * scale;
  const spkrCoverageRadius = (height - talkerHeight * Math.tan(coverageAngle * Math.PI / 180));

  const dispatch = useDispatch();

  useLayoutEffect(() => {
    const cx = round(tableSize.length * 2) / 4;
    const cy = round(tableSize.width * 2) / 4;
    setTableCenter({ x: cx, y: cy });

    const tx = bbox.cx - cx + currentTable.transform.x * scale;
    const ty = bbox.cy - cy + currentTable.transform.y * scale;
    const ta = `${currentTable.transform.a} ${cx} ${cy}`;
    setTableTransfrom({ x: tx, y: ty, a: ta });

    setIconTransform({
      x: cx + tableSize.length / 2 - iconMargin,
      y: cy,
      scale: iconScale
    });
    // eslint-disable-next-line
  }, [currentTable.shape, currentTable.size.length, currentTable.size.width, currentTable.transform, bbox.cx, bbox.cy, scale]);

  const { corner = null, leftCorner = null, rightCorner = null, length = tableSize.length, bow = null, width = tableSize.width } = pathProps(tableSize);

  // Calculate the tablebox - used for tabletop mic calculations 
  const tableBox = useMemo(() => ({
    x: tableSize.length + 2 * (chair.size - chair.clearance),
    y: tableSize.width + 2 * (chair.size - chair.clearance)
    // eslint-disable-next-line
  }), [tableSize.length, tableSize.width]);

  // Generate the table
  const table = useMemo(() => {
    // Generate the Table path
    const slope = clamp((tableSize.width / 2 - 0.4572 * scale) / length, 0, maxSlope);
    const legWidth = 0.6096 * scale;
    const legLength = length - legWidth;
    const x0 = tableCenter.x - length / 2;
    const x1 = slope ? (width / 2 - legWidth + corner + slope * x0) / slope : x0;
    const innerTapper = slope * Math.min(x1 - x0, legLength);
    const innerCorner = maxSlope && (x1 - x0 <= legLength) ? 0 : clamp(width - legWidth * 2 + corner * 2, 0, corner) / 2;
    const inner = (notched && width >= (legWidth - corner) * 2 && innerTapper >= 0) ? [
      `v${corner * 2 - legWidth}`,
      `a${corner},${corner} 0 0 1 ${corner},-${corner}`,
      `l${Math.min(legLength + innerCorner, maxSlope ? x1 - x0 : length)} ${-innerTapper}`,
      `a${innerCorner},${innerCorner} 0 0 0 ${innerCorner},-${innerCorner}`,
      `v${maxSlope && (x1 - x0 <= legLength) ? 0 : (legWidth - corner + innerCorner + innerTapper) * 2 - width}`,
      `a${innerCorner},${innerCorner} 0 0 0 -${innerCorner},-${innerCorner}`,
      `l${-Math.min(legLength + innerCorner, maxSlope ? x1 - x0 : length)} ${-innerTapper}`,
      `a${corner},${corner} 0 0 1 -${corner},-${corner}`,
      `v${corner * 2 - legWidth}`] :
      [`v${- width}`];


    const tapper = maxSlope ? slope * length : 0;
    const rightWidth = maxSlope ? width - tapper * 2 : rightCorner ? 0 : null;
    const originX = tableCenter.x - (length / 2) - rightCorner / 2 + leftCorner / 2;
    const originY = tableCenter.y - (width / 2) - (leftCorner ?? corner);

    const path = [
      `m${originX} ${originY}`,
      `q${length / 2},${bow ? -bow : tapper / 2} ${length},${tapper}`,
      `a${rightCorner ?? corner},${rightCorner ?? corner} 0 0 1 ${rightCorner ?? corner},${rightCorner ?? corner}`,
      `v${rightWidth ?? width}`,
      `a${rightCorner ?? corner},${rightCorner ?? corner} 0 0 1 -${rightCorner ?? corner},${rightCorner ?? corner}`,
      `q${-length / 2},${bow ?? tapper / 2} ${-length},${tapper}`,
      `a${leftCorner ?? corner},${leftCorner ?? corner} 0 0 1 -${leftCorner ?? corner},-${leftCorner ?? corner}`,
      `${inner.join()}`,
      `a${leftCorner ?? corner},${leftCorner ?? corner} 0 0 1 ${leftCorner ?? corner},-${leftCorner ?? corner}z`,
    ].join();

    const { chairArray = [], chairSpacing = {} } = chairs(tableSize, chair, slope);
    const remove = difference(currentTable.ghostChairs, chairArray.map(x => x.id));
    remove.map(chair => dispatch(ReducersIndex.container.setTable({ chair: chair })));

    // HELPER function to generate device paths
    const generateDevicePath = (isTableMic = false, isSpeaker = false) => {
      const deviceFromEdge = (constants.deviceToEdge(isTableMic, indexOf(acoustics, currentAcoustics)) - (isSpeaker ? .865 : 0)) * scale;

      // Get coverage radius based on device type
      const coverageRadius = isSpeaker ? spkrCoverageRadius : micCoverageRadius;

      // Get device path properties
      const pathBox = isSpeaker ? null : tableBox;

      const {
        devicePathLength = 0,
        devicePathWidth = 0,
        devicePathCorner = 0,
        devicePathRightCorner = 0,
        devicePathBow = 0
      } = devicePathProps(tableSize, chairSpacing, pathBox, deviceFromEdge, coverageRadius, legWidth, isTableMic) ?? {};

      // Calculate path parameters
      const devicePathTapper = maxSlope && devicePathWidth !== 0 ? slope * devicePathLength : 0;

      // Adjust parameters based on device type
      let devicePathRightWidth;
      if (isSpeaker) {
        devicePathRightWidth = (maxSlope ? devicePathWidth - devicePathTapper * 2 : 0) + chair.size;
      } else {
        devicePathRightWidth = maxSlope ? devicePathWidth - devicePathTapper * 2 : 0;
      }

      // Calculate origin points
      let devicePathOriginX, devicePathOriginY;

      if (isSpeaker) {
        devicePathOriginX = endTable
          ? tableCenter.x - devicePathLength / 2 + chair.size / 2
          : tableCenter.x + devicePathLength / 2 + (devicePathRightCorner ?? devicePathCorner);

        devicePathOriginY = endTable && (devicePathRightCorner !== 0 || devicePathTapper !== 0 || devicePathWidth !== 0)
          ? deviceFromEdge - chair.size / 2
          : tableCenter.y;
      } else {
        devicePathOriginX = endTable
          ? tableCenter.x - devicePathLength / 2 - devicePathRightCorner / 2
          : tableCenter.x + devicePathLength / 2 + (devicePathRightCorner ?? devicePathCorner);

        devicePathOriginY = endTable && (devicePathRightCorner !== 0 || devicePathTapper !== 0 || devicePathWidth !== 0)
          ? deviceFromEdge
          : tableCenter.y;
      }

      // Generate path string
      let path = [`m${devicePathOriginX} ${devicePathOriginY}`];

      if (endTable) {
        path.push(...[
          `q${devicePathLength / 2},${devicePathBow ? -devicePathBow : devicePathTapper / 2} ${devicePathLength},${devicePathTapper}`,
          `a${devicePathRightCorner ?? devicePathCorner},${devicePathRightCorner ?? devicePathCorner} 0 0 1 ${devicePathRightCorner ?? devicePathCorner},${devicePathRightCorner ?? devicePathCorner}`,
          `v${devicePathRightWidth > 0 ? devicePathRightWidth : devicePathWidth}`,
          `a${devicePathRightCorner ?? devicePathCorner},${devicePathRightCorner ?? devicePathCorner} 0 0 1 -${devicePathRightCorner ?? devicePathCorner},${devicePathRightCorner ?? devicePathCorner}`,
          `q${-devicePathLength / 2},${devicePathBow ? devicePathBow : devicePathTapper / 2} ${-devicePathLength},${devicePathTapper}`,
        ]);
      } else {
        path.push(...[
          `v${devicePathWidth / 2}`,
          `a${devicePathRightCorner ?? devicePathCorner},${devicePathRightCorner ?? devicePathCorner} 0 0 1 -${devicePathRightCorner ?? devicePathCorner},${devicePathRightCorner ?? devicePathCorner}`,
          `q${-devicePathLength / 2},${devicePathBow} ${-devicePathLength},${0}`,
          `a${devicePathCorner},${devicePathCorner} 0 0 1 -${devicePathCorner},-${devicePathCorner}`,
          `v${-devicePathWidth}`,
          `a${devicePathCorner},${devicePathCorner} 0 0 1 ${devicePathCorner},-${devicePathCorner}`,
          `q${devicePathLength / 2},${-devicePathBow} ${devicePathLength},${0}`,
          `a${devicePathRightCorner ?? devicePathCorner},${devicePathRightCorner ?? devicePathCorner} 0 0 1 ${devicePathRightCorner ?? devicePathCorner},${devicePathRightCorner ?? devicePathCorner}`,
          `z`
        ]);
      }

      return path.join(' ');
    };

    return {
      path,
      chairs: chairArray,
      micPath: generateDevicePath(isTableMic, false),
      spkrPath: generateDevicePath(false, true)
    }
    // eslint-disable-next-line
  }, [corner, leftCorner, rightCorner, length, bow, width, tableCenter.x, tableCenter.y, microphoneModel, micCoverageRadius]);

  // keep track of sides that have chairs 
  const [chaired, setChaired] = useState({ top: true, bottom: true, left: true, right: true });
  useEffect(() => {
    // Create initial state object
    const chairState = {
      top: false,
      bottom: false,
      left: false,
      right: false
    };
    const timer = setTimeout(() => {
      if (tableRef.current && currentTable.shape !== 'round') {
        const chairs = Array.from(tableRef.current.getElementsByClassName('chair'));
        // Process each chair
        chairs.forEach(chair => {
          if (!currentTable.ghostChairs.includes(chair.id)) {
            const position = chair.id.split('-')[1].match(/[a-zA-Z]+/g)[0];
            if (position in chairState) {
              chairState[position] = true;
            }
          }
        });
        // Only update state if it has actually changed
        if (!isEqual(chairState, chaired)) {
          setChaired(chairState);
        };
      };
    }, 0);
    return () => clearTimeout(timer);
  }, [currentTable.ghostChairs, currentTable.shape, chaired]);

  const { length: hmax, width: vmax } = mapValues(tableSize, v => v / 2);
  const center2wall = mapValues(chaired, (x, k) => (['left', 'right'].includes(k) ? hmax : vmax) + x * table2wall);
  const { left, top, right, bottom } = center2wall;

  // Moving limits
  const [limits, setLimits] = useState({ hor: [], ver: [], angle: [] });
  const [moving, setMoving] = useState(false);
  const mouse = useMousePosition(true, true);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [anchor, setAnchor] = useState({ x: 0, y: 0 });
  const [transform, setTransform] = useState({ x: 0, y: 0, a: 0 });

  const radius = (side = null) => {
    const adj = table2wall * (!side || chaired[side] ? 1 : some(pick(chaired, ['left', 'right'].includes(side) ? ['top', 'bottom'] : ['left', 'right'])) ? .53 : 0);
    return Math.hypot(max([hmax, vmax]) + adj, min([vmax, hmax]));
  };

  /**
 * @function setMotionLimits
 * @description Calculates and sets the motion limits for table movement and rotation.
 */
  const setMotionLimits = () => {
    if (tableRef.current) {
      // Linear limits
      const angle = Math.abs(tableTransform.a.split(' ')[0] * Math.PI / 180);
      const cx = tableCenter.x + tableTransform.x;
      const cy = tableCenter.y + tableTransform.y;
      const cosA = Math.cos(angle);
      const sinA = Math.sin(angle);

      const ps = [
        [cx - left * cosA + top * sinA, cy - top * cosA - left * sinA],
        [cx + right * cosA + top * sinA, cy - top * cosA + right * sinA],
        [cx + right * cosA - bottom * sinA, cy + bottom * cosA + right * sinA],
        [cx - left * cosA - bottom * sinA, cy + bottom * cosA - left * sinA]
      ];

      const adj = table2wall * (Math.abs(angle) <= Math.PI * .25 || Math.abs(angle) >= Math.PI * .75 ? sinA : cosA);

      const minH = minBy(ps, x => x[0])[0] - bbox.x0 - 2 + adj;
      const maxH = bbox.x1 - maxBy(ps, x => x[0])[0] - 3 + adj;
      const minV = minBy(ps, y => y[1])[1] - bbox.y0 - 2 + adj;
      const maxV = bbox.y1 - maxBy(ps, y => y[1])[1] - 3 + adj;

      // Rotation limits 
      let rotationLimits = [-180, 180];
      const updateRotationLimits = (angles) => rotationLimits = [
        minBy([min(angles), min(rotationLimits)], v => (Math.abs(v) + 360) % 360),
        minBy([max(angles), max(rotationLimits)], v => (Math.abs(v) + 360) % 360)
      ];

      if (currentTable.shape !== 'round') {
        // TOP SIDE
        if (cy - bbox.y0 < radius()) {
          let angles;
          if (cy - bbox.y0 < vmax + table2wall + 2) {
            const angle = Math.abs(round(Math.atan2((cy - vmax - bbox.y0), hmax) * 36 / Math.PI) * 5);
            angles = [-angle, angle];
          } else {
            const [b1, b2] = ['left', 'right'].map((side, index) =>
              (index % 2 ? 1 : -1) * (round((Math.atan2(top, center2wall[side]) + Math.acos((cy - bbox.y0) / radius(side))) * 36 / Math.PI) * 5 - 90)
            );
            angles = [isNaN(b1) ? -(180 + b2) : b1, isNaN(b2) ? 180 - b1 : b2];
          };

          updateRotationLimits(angles);
        };

        // BOTTOM SIDE
        if (bbox.y1 - cy < radius()) {
          let angles;
          if (bbox.y1 - cy < vmax + table2wall + 3) {
            const angle = Math.abs(round(Math.atan2((bbox.y1 - vmax - cy), hmax) * 36 / Math.PI) * 5);
            angles = [-angle, angle];
          } else {
            const [b1, b2] = ['left', 'right'].map((side, index) =>
              (index % 2 ? -1 : 1) * (round((Math.atan2(bottom, center2wall[side]) + Math.acos((bbox.y1 - cy) / radius(side))) * 36 / Math.PI) * 5 - 90)
            );
            angles = [isNaN(b1) ? 180 - b2 : b1, isNaN(b2) ? - (180 + b1) : b2];
          };
          updateRotationLimits(angles);
        };

        // LEFT SIDE
        if (cx - bbox.x0 < radius()) {
          let angles;
          if (cx - bbox.x0 < hmax + table2wall + 2) {
            const angle = Math.abs(Math.atan2((cx - hmax - bbox.x0), vmax) * 180 / Math.PI);
            angles = [-angle, angle];
          } else {
            const [b1, b2] = ['top', 'bottom'].map((side, index) =>
              (index % 2 ? -1 : 1) * (round((Math.atan2(left, center2wall[side]) + Math.acos((cx - bbox.x0) / radius(side))) * 36 / Math.PI) * 5 - 90)
            );
            angles = [isNaN(b1) ? 180 + b2 : b1, isNaN(b2) ? 180 - b1 : b2];
          };
          updateRotationLimits(angles);
        };

        // RIGHT SIDE
        if (bbox.x1 - cx < radius()) {
          let angles;
          if (bbox.x1 - cx < hmax + table2wall + 2) {
            const angle = Math.abs(Math.atan2((bbox.x1 - hmax - cx), vmax) * 180 / Math.PI);
            angles = [-angle, angle];
          } else {
            const [b1, b2] = ['top', 'bottom'].map((side, index) =>
              (index % 2 ? 1 : -1) * (round((Math.atan2(right, center2wall[side]) + Math.acos((bbox.x1 - cx) / radius(side))) * 36 / Math.PI) * 5 - 90)
            );
            angles = [isNaN(b1) ? 180 + b2 : b1, isNaN(b2) ? 180 - b1 : b2];
          };
          updateRotationLimits(angles);
        };
      };

      // Set the move limits
      setLimits({
        hor: [min([-minH, 0]), max([maxH, 0])],
        ver: [min([-minV, 0]), max([maxV, 0])],
        minAngle: min(rotationLimits),
        maxAngle: max(rotationLimits)
      });
    };
  };

  /**
 * @object moveHandlers
 * @description Object containing event handlers for table movement and rotation.
 * @property {Function} onPointerDown - Handles the start of a move/rotate action
 * @property {Function} onPointerMove - Handles the movement/rotation of the table
 * @property {Function} onPointerUp - Handles the end of a move/rotate action
 */
  const moveHandlers = {
    onPointerDown: (e) => {
      const parent = e.target.closest('.action');
      const { left, right, top, bottom } = parent.getBoundingClientRect();
      moveRef.current.setPointerCapture(e.pointerId);
      moveRef.current.action = parent.id;
      setOffset({ x: e.clientX - (right + left) / 2, y: e.clientY - (top + bottom) / 2 });
      setAnchor(mouse);
      setTransform({ ...tableTransform, a: tableTransform.a.split(' ')[0] * Math.PI / 180 });
      setMotionLimits();
      setMoving(true);
      dispatch(ReducersIndex.uiReducers.setAction('moveTable'));
      e.stopPropagation();
    },
    onPointerMove: (e) => {
      if (!moving) return
      let { x = 0, y = 0 } = mouse ?? {};
      if (!x || !y) return;

      if (moveRef.current.action === 'move') {
        x = clamp(x - anchor.x, ...limits.hor) + transform.x;
        y = clamp(y - anchor.y, ...limits.ver) + transform.y;
        setTableTransfrom({ ...tableTransform, x: x, y: y });
      } else {
        x -= tableTransform.x - Math.sin(transform.a) * iconMargin + offset.x;
        y -= tableTransform.y + Math.cos(transform.a) * iconMargin + offset.y;
        let angle = round(Math.atan2((y - tableCenter.y), (x - tableCenter.x)) * 36 / Math.PI) * 5;
        const sameSign = Math.sign(limits.minAngle) === Math.sign(limits.maxAngle);
        if (sameSign) {
          if (limits.minAngle === 0 && limits.maxAngle === 0) {
            angle = 0;
          } else {
            angle = angle <= limits.minAngle ? clamp(angle, -180, limits.minAngle) : angle >= limits.maxAngle && angle <= 180 ? clamp(angle, limits.maxAngle, 180) : 0;
          };
        } else {
          angle = clamp(angle, limits.minAngle, limits.maxAngle)
        };
        setTableTransfrom({ ...tableTransform, a: `${angle} ${tableCenter.x} ${tableCenter.y}` });
      };
    },
    onPointerUp: (e) => {
      moveRef.current.releasePointerCapture(e.pointerId);
      moveRef.current.action = null;
      setMoving(false);
      dispatch(ReducersIndex.uiReducers.setAction());

      const x = round((tableTransform.x - bbox.cx + tableCenter.x) / scale, 4);
      const y = round((tableTransform.y - bbox.cy + tableCenter.y) / scale, 4);
      dispatch(ReducersIndex.container.setTable({ transform: { x: x, y: y, a: +tableTransform.a.split(' ')[0] } }));
    }
  };

  const active = useSelector(state => getConditionResult(state, { ...props.active, area: areaId }));
  // Prevent drawing the table if dimension are below minimum
  const hasValidSize = every(keys(currentTable.size), prop => currentTable.size[prop] >= minimumTableSize[prop]);
  const visible = Boolean(currentTable.shape) && hasValidSize;
  const filled = useSelector(state => getConditionResult(state, { ...props.filled }));

  const moveRef = useRef();
  const tableRef = useRef();


  return (
    <>
      {visible && <Table.Group id={`${areaId}-Furniture`} ref={tableRef} tx={tableTransform} >
        {table.chairs.map((ch, i) => {
          const ghosted = currentTable.ghostChairs.includes(ch.id)
          const state = chair.state(ghosted, filled);
          const angle = (tableTransform?.a?.split(' ')[0] ?? 0) * Math.PI / 180;
          const xx = ch.x * Math.cos(angle) - ch.y * Math.sin(angle) + tableCenter.x + tableTransform.x
          const yy = ch.x * Math.sin(angle) + ch.y * Math.cos(angle) + tableCenter.y + tableTransform.y
          const rr = chair.size * .51;
          return (xx - rr >= bbox.x0 && xx + rr <= bbox.x1 && yy - rr >= bbox.y0 && yy + rr <= bbox.y1) && (
            <Chair key={ch.id} className="chair" state={state} arms={true} size={chair.size} {...assignWith({ ...ch }, tableCenter, (a, b) => a + b)} />
          )
        })}
        <Table.Path active={active} filled={filled} path={table.path} />
        <Table.DevicePath id={'micPath'} path={table.micPath} />
        <Table.DevicePath id={'spkrPath'} path={table.spkrPath} />
        {active && <Table.ButtonWrapper ref={moveRef} transform={iconTransform} pointer={moving} {...moveHandlers}>
          <Move state={iconThemes['button_selected']} transform={`translate(0, -${iconMargin * .8})`} center />
        </Table.ButtonWrapper>}
        {active && <Table.ButtonWrapper ref={moveRef} transform={iconTransform} pointer={moving} {...moveHandlers}>
          <Rotate state={iconThemes['button_selected']} transform={`translate(0, ${iconMargin * .8})`} center />
        </Table.ButtonWrapper>}
      </Table.Group>
      }
    </>
  )
};

/**
 * @component Table.Group
 * @description Styled component for the table group
 */
Table.Group = styled.g.attrs(props => ({
  transform: `translate(${props.tx.x}, ${props.tx.y}) rotate(${props.tx.a ?? 0})`,
}))`
  stroke-width: 3px;
  shape-rendering: crispEdges;  
`;

/**
 * @component Table.Path
 * @description Styled component for the table path
 */
Table.Path = styled.path.attrs(props => ({
  d: props.path
}))`
  stroke: ${props => props.active ? 'var(--app-red)' : 'var(--app-dark-gray)'};
  fill: ${props => props.filled ? 'var(--app-light-gray)' : 'none'};
  shape-rendering: crispEdges;
  pointer-events: none;

`

/**
 * @component Table.devicePath
 * @description Styled component for the device path
 */
Table.DevicePath = styled.path.attrs(props => ({
  d: props.path
}))`
  stroke: transparent;
  stroke-width: 0px;
  fill: none;
  shape-rendering: crispEdges;
  pointer-events: none;
`

/**
 * @component Table.ButtonWrapper
 * @description Styled component for the table action buttons (move/rotate)
 */
Table.ButtonWrapper = styled.g.attrs(({ transform }) => ({
  transform: `translate(${transform.x}, ${transform.y}) scale(${transform.scale})`
}))`
cursor: ${props => props.pointer ? 'all-scroll' : 'pointer'};
:hover {
    cursor: 'pointer';
  };
`

export default Table;
export { constants as TableConstants }